import { useEffect, useMemo } from 'react'

import {
  MEETINGS_DIMENSIONS,
  MEETINGS_MEASURES,
  MEETINGS_USERS_DIMENSIONS,
} from 'cube/constants'
import { MeetingCountQuery } from 'cube/queries'
import moment from 'moment'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import { IQueryConfig } from 'api/report/query/types'
import useTimePeriod from 'hooks/useTimePeriod'

import { formatPageFilters } from '../MeetingLineChart/useMeetingCount.hook'

const QUERY_LIMIT = 10
export interface ITopPerformers {
  metric?: TopPerformer[]
  isLoading: boolean
  isError: boolean
}

interface TopPerformer {
  user: number
  value: number
}

const formatTableData = (resultSet) =>
  resultSet?.map((data) => ({
    user: parseInt(data?.[MEETINGS_USERS_DIMENSIONS.AGENT_ID]),
    value: parseInt(data?.[MEETINGS_MEASURES.MEETING_COUNT]),
  }))

export const useMeetingsTopPerformers = (
  queryConfig: IQueryConfig
): ITopPerformers => {
  const timePeriod = useTimePeriod()
  const pageFilters = formatPageFilters(queryConfig.compoundQuery)

  const cubeQuery = new CubeQueryBuilder({
    query: MeetingCountQuery,
  })
    .withName('MeetingsTopPerformers')
    .withFilters(pageFilters)
    .withDimensions([MEETINGS_USERS_DIMENSIONS.AGENT_ID])
    .withLimit(QUERY_LIMIT)
    .withTimeDimensions([
      {
        dimension: MEETINGS_DIMENSIONS.SCHEDULED_AT,
        dateRange: [
          moment(timePeriod.startDate).format('YYYY-MM-DD'),
          moment(timePeriod.endDate).format('YYYY-MM-DD'),
        ],
      },
    ])
    .build()

  const {
    data: resultSet,
    refetch,
    isLoading: isLoading,
    isError: isError,
  } = useCubeQuery({
    query: cubeQuery,
  })

  const topPerformers = useMemo(
    () => formatTableData(resultSet?.tablePivot()),
    [resultSet]
  )
  const metric = useMemo(() => topPerformers ?? null, [topPerformers])

  useEffect(() => {
    refetch()
  }, [queryConfig.compoundQuery, refetch])

  return useMemo(
    () => ({ metric, isLoading, isError }),
    [metric, isLoading, isError]
  )
}

export default useMeetingsTopPerformers
